import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { Modal, OTPStep } from '@unified-checkout/design-system';

import type {
  IAddEmailProps,
  IResendOtpPayload,
  IVerifyEmailPayload,
} from '@app/dashboard/types/innerLayout/notifications/modals/add-email';
import {
  resendOtp,
  verifyEmail,
} from '@app/dashboard/services/inner_layout/notifications';
import { useLangStore } from '@app/dashboard/store/lang';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  ADD_EMAIL_FIELD_INITIAL_STATE,
  NOTIFICATION_CATEGORIES,
} from '@app/dashboard/constants/settings/notifications';
import { ONE_MINUTE, OTP_LENGTH } from '@app/dashboard/constants/shared';
import { SMALL_MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { notify } from '@app/dashboard/utils/notifier';

import styles from './AddEmail.module.scss';

const AddEmail = ({
  isModalOpen,
  setIsModalOpen,
  setOpenAddEmailInput,
  email,
  setFields,
}: IAddEmailProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();

  const [otp, setOtp] = useState('');
  const [otpResetTimerKey, setOtpResetTimerKey] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const isMobile = screenWidth <= SMALL_MOBILE_BREAKPOINT;

  const { isPending: isResendingOtp, mutate: resendOtpMutation } = useMutation({
    mutationKey: [QUERY_KEYS.RESEND_OTP],
    mutationFn: (payload: IResendOtpPayload) => resendOtp(payload),
    onSuccess: () => {
      setOtpResetTimerKey((prev) => prev + 1);
      notify.success(t('form.otpResent'));
    },
    onError: (axiosErr: AxiosError<{ message?: string }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }
      notify.error(message);
    },
  });

  const { isPending: isVerifyEmailLoading, mutate: verifyEmailMutation } =
    useMutation({
      mutationFn: (payload: IVerifyEmailPayload) => verifyEmail(payload),
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EMAILS] });
        notify.success(t('innerLayout.notificationsPage.toaster.emailAdded'));
        setFields(ADD_EMAIL_FIELD_INITIAL_STATE(t));
        setIsModalOpen(false);
        setOpenAddEmailInput(false);
      },
      onError: (axiosErr: AxiosError<{ message?: string }>) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data) {
          const data = axiosErr.response.data as { message?: string } | any;
          if (data?.message) {
            message = data.message;
          } else if (typeof data === 'object') {
            message = data[lang];
          }
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }

        setServerError(message);
      },
    });

  const handleResendOtp = () => {
    resendOtpMutation({ email });
    setServerError(undefined);
    setOtp('');
  };

  const validHandler = (valid: boolean) => {
    setIsValid(valid);
    setServerError(undefined);
  };

  const handleSubmit = () => {
    setServerError(undefined);
    verifyEmailMutation({
      payload: { email, category: NOTIFICATION_CATEGORIES.NOTIFICATION },
      otp,
    });
  };

  useEffect(() => {
    setOtpResetTimerKey((prev) => prev + 1);
    setOtp('');
    setIsValid(false);
    setServerError(undefined);
  }, [isModalOpen]);

  return (
    <Modal
      open={isModalOpen}
      title={t('innerLayout.notificationsPage.modals.otp.title')}
      supportingText={t('innerLayout.notificationsPage.modals.otp.subtitle', {
        email,
      })}
      isOverlayAboveModal
      btnsProps={[
        {
          text: t('innerLayout.notificationsPage.modals.actions.submit'),
          variant: 'contained',
          color: 'primary',
          onClickHandler: handleSubmit,
          disabled: isResendingOtp || !isValid || isVerifyEmailLoading,
          loading: isVerifyEmailLoading,
        },
        {
          text: t('innerLayout.notificationsPage.modals.actions.cancel'),
          variant: 'outlined',
          color: 'secondary',
          onClickHandler: () => setIsModalOpen(false),
          disabled: isResendingOtp || isVerifyEmailLoading,
        },
      ]}
    >
      <div className={styles['otp-wrapper']}>
        <OTPStep
          otp={otp}
          setOtp={setOtp}
          onValid={validHandler}
          onResendOTP={handleResendOtp}
          otpResetTimerKey={otpResetTimerKey}
          resendText={t('form.resendCodeIn')}
          resendBtnText={isResendingOtp ? t('loading') : t('form.resend')}
          resendTimer={ONE_MINUTE}
          length={OTP_LENGTH}
          loading={isResendingOtp || isVerifyEmailLoading}
          error={serverError}
          {...(isMobile && {
            customStyle: {
              input: {
                width: '2.5rem',
                height: '2.5rem',
              },
              resendAction: {},
            },
          })}
        />
      </div>
    </Modal>
  );
};

export default AddEmail;
